<script setup lang="ts">
import { UiLinkVariant } from '@afs/design-system'
import type { UiTagProps } from '@afs/design-system/components/atoms/UiTag/interface.d.ts'
import { useData } from 'vike-vue/useData'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AdvicePageData } from './+data'
import type { UiFiltersListProps } from '@afs/design-system/components/molecules/UiFiltersList/interface.js'
import { useFormatSeoContent } from '@/composables/useFormatSeoContent'
import { useGtmOnPageMounted } from '@/composables/useGtmTag'

const { t } = useI18n()

const data = useData<AdvicePageData>()
useFormatSeoContent(data.advice.seoTitle, data.advice.hero.subtitle)
useGtmOnPageMounted('other')

const selectedFilter = ref<UiTagProps | null>()
if (data.advice?.filtersBlock.filters) {
  selectedFilter.value = data.advice.filtersBlock.filters.find((filter) => filter?.id === 'all') || null
}

const filteredArticles = computed(() => {
  if (selectedFilter.value?.id === 'all') {
    return data.advice?.allArticles || []
  } else {
    return data.advice?.allArticles?.filter((article) => article.productId === selectedFilter.value?.id) || []
  }
})

const initialArticlesCount = computed(() => (selectedFilter.value?.id === 'all' ? 6 : 9))

// eslint-disable-next-line vue/no-ref-object-reactivity-loss
const displayedArticles = ref(filteredArticles.value.slice(0, initialArticlesCount.value))

// eslint-disable-next-line vue/no-ref-object-reactivity-loss
const currentIndex = ref(initialArticlesCount.value)

/*
const showMoreBtn = computed(() => currentIndex.value < (filteredArticles.value?.length || 0))

const handleLoadMore = () => {
  if (filteredArticles.value) {
    displayedArticles.value = [
      ...displayedArticles.value,
      ...filteredArticles.value.slice(currentIndex.value, currentIndex.value + 6),
    ]
    currentIndex.value += 6
  }
}
*/

// Utiliser computed pour afficher les articles

const showMoreBtn = computed(() => currentIndex.value < (filteredArticles.value?.length || 0))

const handleLoadMore = () => {
  if (filteredArticles.value) {
    // On met à jour displayedArticles avec la nouvelle valeur
    displayedArticles.value = [
      ...displayedArticles.value,
      ...filteredArticles.value.slice(currentIndex.value, currentIndex.value + 6),
    ]
    currentIndex.value += 6
  }
}
const handleSelectFilter = (filter: UiTagProps) => {
  selectedFilter.value = filter
  currentIndex.value = initialArticlesCount.value
  displayedArticles.value = filteredArticles.value.slice(0, initialArticlesCount.value)
}

const breadcrumb = {
  ariaLabel: t('breadcrumb.ariaLabel'),
  items: [
    { text: t('page.accueil'), to: '/' },
    {
      text:
        data.advice?.breadcrumbTitle && data.advice.breadcrumbTitle.length > 40
          ? `${data.advice?.breadcrumbTitle.slice(0, 40)}...`
          : data.advice?.breadcrumbTitle,
    },
  ],
}
</script>
<template>
  <div
    v-if="data.advice"
    class="Advices"
  >
    <molecules-ui-breadcrumb
      v-bind="breadcrumb"
      class="Advices__breadcrumb"
    />
    <organisms-ui-hero v-bind="data.advice.hero" />
    <div class="container my-8">
      <div
        v-if="data.advice.filtersBlock && selectedFilter"
        class="mb-7"
      >
        <molecules-ui-filters-list
          v-bind="data.advice.filtersBlock as UiFiltersListProps"
          v-model="selectedFilter"
          @update:model-value="handleSelectFilter"
        />
      </div>
      <section
        v-if="selectedFilter?.id === 'all'"
        class="Advices__highlight grid mb-8"
      >
        <div
          v-if="data.advice.promotionalBlock"
          class="Advices__highlight__left"
        >
          <molecules-ui-focus v-bind="data.advice.promotionalBlock" />
        </div>
        <div
          v-if="data.advice.topArticles"
          class="Advices__highlight__right"
        >
          <h2 class="Advices__highlight__right__title heading-2 color-blue-01">
            {{ data.advice.topArticlesTitle }}
          </h2>
          <ul class="Advices__highlight__right__list g-4 custom-scrollbar-h">
            <li
              v-for="article in data.advice.topArticles"
              :key="article.id"
              class="Advices__highlight__right__item mb-4"
            >
              <molecules-ui-card v-bind="article" />
            </li>
          </ul>
        </div>
      </section>
      <section
        v-if="selectedFilter?.id === 'all'"
        class="Advices__lastNews grid gy-7 gx-6"
      >
        <div
          v-if="data.advice.supportBlock"
          class="Advices__lastNews__top radius-m bg-yellow-01"
        >
          <h3 class="mb-4 heading-3 color-blue-01">{{ data.advice.supportBlock.title }}</h3>
          <ul class="Advices__lastNews__list g-2">
            <li
              v-for="information in data.advice.supportBlock.cta"
              :key="information.id"
              class="Advices__lastNews__item"
            >
              <atoms-ui-link
                v-bind="information"
                :icon="information.picto"
                :variant="UiLinkVariant.FLAT"
              />
            </li>
          </ul>
        </div>
        <div
          v-if="data.advice.allArticles"
          class="Advices__lastNews__bottom"
        >
          <advices-previews-list
            :title="t('advices.latestNews.title')"
            :count="data.advice.allArticles.length"
            :show-more-btn="showMoreBtn"
            :articles="displayedArticles"
            @previews-list::load-more="handleLoadMore"
          />
        </div>
      </section>
      <section
        v-if="selectedFilter?.id !== 'all'"
        class="grid"
      >
        <advices-articles-list
          class="Advices__results"
          :active-filter="selectedFilter?.text || 'all'"
          :count="filteredArticles.length"
          :show-more-btn="showMoreBtn"
          :articles="displayedArticles"
          @articles-list::load-more="handleLoadMore"
        />
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'config/spaces';
@use 'config/colors';
@use 'utilities/mq';

.Advices__breadcrumb {
  position: absolute;
  top: 0;
}

.Advices__lastNews__top {
  grid-column: span 4;
  padding: map.get(spaces.$sizes, '7') map.get(spaces.$sizes, '5') map.get(spaces.$sizes, '5');

  @include mq.media('>=medium') {
    grid-column: 9 / span 4;
    grid-row: 1;
    height: fit-content;
  }
}

.Advices__lastNews__list {
  display: flex;
  flex-direction: column;
}

.Advices__lastNews__bottom {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 1 / span 8;
    grid-row: 1;
  }
}

.Advices__results {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: span 12;
  }

  @include mq.media('>=large') {
    grid-column: 2 / span 10;
  }
}

.Advices__highlight {
  @include mq.media('<medium') {
    row-gap: map.get(spaces.$sizes, '8');
  }

  @include mq.media('>=medium') {
    grid-column: 2 / -1;
  }
}

.Advices__highlight__left {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 1 / span 4;
    margin-top: map.get(spaces.$sizes, '8');
  }
}

.Advices__highlight__right {
  grid-column: span 4;
  display: flex;
  flex-direction: column;
  gap: map.get(spaces.$sizes, '5');

  @include mq.media('>=medium') {
    position: relative;
    gap: map.get(spaces.$sizes, '6');
    grid-column: 5 / -1;
    max-width: calc((336px * 3) + 72px);

    &::before {
      content: '';
      z-index: 1;
      position: absolute;
      top: 0;
      left: map.get(spaces.$sizes, '5') * -1;
      width: map.get(spaces.$sizes, '5');
      height: calc(100% - 8px);
      background-image: linear-gradient(
        to right,
        map.get(colors.$basics, 'white'),
        map.get(colors.$basics, 'transparent')
      );
    }
  }

  .Card {
    height: 100%;
  }
}

.Advices__highlight__right__title {
  height: 32px;
  text-align: center;
}

.Advices__highlight__right__list {
  display: flex;
  overflow-x: auto;
  margin-inline-start: calc(map.get(spaces.$sizes, '5') * -1);
  padding-inline: map.get(spaces.$sizes, '5');

  @include mq.media('<medium') {
    margin-inline-end: calc(map.get(spaces.$sizes, '5') * -1);
  }
}

.Advices__highlight__right__item {
  flex: 0 0 300px;

  @include mq.media('>=medium') {
    flex: 0 0 336px;
  }
}
</style>
